import { request, Group, Player } from "./client";
import { registerMessages } from "./errors";

registerMessages({
  "month is already paid — schedule entry cannot be removed":
    "По месяцу уже есть счёт — убрать его из плана нельзя",
  already_invoiced: "Счёт за этот месяц уже выставлен",
  no_amount: "Не задана стоимость: укажите свою сумму месяца или ставку группы",
  no_parent: "У ребёнка нет родителя — выставлять счёт некому",
  "failed to list schedule": "Не удалось загрузить график счетов",
  "failed to read schedule": "Не удалось загрузить график счетов",
  "failed to upsert schedule entry": "Не удалось сохранить месяц в плане",
  "failed to delete schedule entry": "Не удалось убрать месяц из плана",
  "failed to check paid status": "Не удалось проверить, есть ли по месяцу счёт",
  "failed to plan cell": "Не удалось добавить месяц в план",
  "failed to issue invoice": "Не удалось выставить счёт",
  "player_id, year (2020..2100), month (1..12) required": "Не указан ребёнок, год или месяц",
  "player_id, year, month required": "Не указан ребёнок, год или месяц",
  "amount must be > 0 when provided": "Сумма должна быть больше нуля",
  "action must be enable|disable": "Некорректное действие с планом",
  "year out of range": "Год должен быть от 2020 до 2100",
  "player_ids required": "Не выбраны дети",
  "month out of range": "Некорректный месяц",
  "failed to load group fees": "Не удалось загрузить ставки групп",
  "failed to load base fee": "Не удалось загрузить базовую ставку",
});

export type PaymentStatus =
  | "pending"
  | "processing"
  | "completed"
  | "manual"
  | "failed"
  | "expired"
  | "cancelled"
  | "refunded";

// One planned (player, month) cell. Amounts are rubles. `amount` is the
// cell's own price; without it the server bills the group's fee, then the
// school base fee. payment_* describe the latest monthly invoice for the cell.
export interface ScheduleEntry {
  id: string;
  player_id: string;
  player_name?: string;
  player_group_id?: string;
  group_name?: string;
  year: number;
  month: number;
  amount?: number | null;
  payment_id?: string;
  payment_status?: PaymentStatus;
  payment_amount?: number;
  created_at: string;
  updated_at: string;
}

export interface GroupCurrentFee {
  group_id: string;
  group_name: string;
  has_override: boolean;
  monthly_fee?: number;
  effective_from?: string;
}

export interface Cell {
  playerID: string;
  year: number;
  month: number;
}

const cellBody = (cell: Cell) => ({ player_id: cell.playerID, year: cell.year, month: cell.month });

export const paymentScheduleApi = {
  listGroups: () => request<Group[] | null>("/api/groups/"),

  listPlayers: () => request<Player[] | null>("/api/players/"),

  listSchedule: (year: number) =>
    request<{ year: number; entries: ScheduleEntry[] | null }>(
      `/api/payments/schedule?${new URLSearchParams({ year: String(year) })}`
    ),

  listGroupFees: () => request<GroupCurrentFee[] | null>("/api/payment-settings/group-fees"),

  getBaseFee: () =>
    request<{ current: { monthly_fee: number } | null }>("/api/payment-settings/base-fee"),

  // Without an amount the server keeps a price the cell already has, so an
  // own price can be replaced but not cleared through this call.
  planMonth: (cell: Cell, amount?: number) =>
    request<ScheduleEntry>("/api/payments/schedule", {
      method: "PUT",
      body: JSON.stringify(amount === undefined ? cellBody(cell) : { ...cellBody(cell), amount }),
    }),

  unplanMonth: (cell: Cell) =>
    request<void>("/api/payments/schedule", { method: "DELETE", body: JSON.stringify(cellBody(cell)) }),

  // Applies to every player × month pair in one call. The server does not
  // check invoices on "disable", so callers must leave invoiced cells out.
  bulk: (input: {
    action: "enable" | "disable";
    year: number;
    playerIDs: string[];
    months: number[];
    amount?: number;
  }) =>
    request<{ action: string; applied: number; failed: number }>("/api/payments/schedule/bulk", {
      method: "POST",
      body: JSON.stringify({
        action: input.action,
        year: input.year,
        player_ids: input.playerIDs,
        months: input.months,
        ...(input.amount === undefined ? {} : { amount: input.amount }),
      }),
    }),

  // Bills the cell right away and notifies the parents; an unplanned cell is
  // added to the plan first.
  issueInvoice: (cell: Cell, adminNote?: string) =>
    request<{ id: string; status: PaymentStatus; amount: number }>("/api/payments/schedule/issue", {
      method: "POST",
      body: JSON.stringify(adminNote ? { ...cellBody(cell), admin_note: adminNote } : cellBody(cell)),
    }),
};
